import { BIF, createWalletStore, splitNetId } from '@halo/wallet-app';
import { shallowCallback } from '@halo/zustand-enhance';
import { genId } from '@halo/utils';
import { useSessionStore } from '@/store/session/store';
import { SyncBackupType, useSettingsStore } from '@/store/settings';
import { useAppStore } from '../store/client';

function request(url: string, method: string, data: any, timeout?: number) {
  return useAppStore.getState().client!.Api.common.request({
    url,
    method,
    body: data,
    timeout,
  });
}

function decrypt(cipherText: string) {
  const client = useAppStore.getState().client;
  if (!client) {
    throw new Error('client not found');
  }

  return client.Api.common.pkeDecrypt(cipherText);
}

interface RequestMessage {
  method: string;
  params?: Record<string, any>;
  businessType?: string;
  businessParams?: Record<string, any>;
}

interface ResponseMessage {
  code: number;
  signature: string;
  data?: Record<string, any>;
}

function appOperation(message: RequestMessage, abortController: AbortController | undefined) {
  function onBeforeUnload(e: BeforeUnloadEvent) {
    e.preventDefault();
    return (e.returnValue = 'Are you sure you want to leave?');
  }

  window.addEventListener('beforeunload', onBeforeUnload, { once: true });

  return new Promise<ResponseMessage>((resolve, reject) => {
    const trace_id = genId();

    function onAbort() {
      request('/api/v1/authorization/cancel', 'POST', {
        trace_id,
      });
      reject(new Error('cancel'));
    }

    abortController?.signal.addEventListener('abort', onAbort);

    const content = {
      params: {},
      businessType: 'common',
      businessParams: {},
      ...message,
    };

    request(
      '/api/v1/authorization/forward',
      'POST',
      {
        trace_id,
        expire_seconds: 120,
        content: JSON.stringify({
          ...content,
          params: JSON.stringify(content.params),
          businessParams: JSON.stringify(content.businessParams),
        }),
      },
      180000
    )
      .then(async (res: any) => {
        const { status, result } = res.data || {};
        if (status !== 9) {
          throw new Error(result);
        }

        const plainText = await decrypt(result);

        return {
          code: status,
          signature: plainText,
        };
      })
      .then(resolve, reject)
      .finally(() => {
        abortController?.signal.removeEventListener('abort', onAbort);
      });
  }).finally(() => {
    window.removeEventListener('beforeunload', onBeforeUnload);
  });
}

export const useWalletStore = createWalletStore({
  baseURL: import.meta.env.VITE_WALLET_URL,
  baseURL_TRANSACTION: import.meta.env.VITE_WALLET_URL_TRANSACTION,
  baseURL_WALLET_LANDING: `https://${import.meta.env.VITE_LANDING_HOST}/w/r`,
  apiKey_TRANSACTION: import.meta.env.VITE_WALLET_TRANSACTION_API_KEY,
  enableTriggers: [
    'createAccountTrigger',
    'deleteAccountTrigger',
    'updateAccountTrigger',
    'changePasswordTrigger',
    'syncDataTrigger',
  ],
  readonlyHandlers: {
    async signTransaction(params, abortController) {
      console.log('debug:sign', params, abortController);
      return await appOperation(params, abortController);
    },
    // 
    async signRawTransaction(params, businessParams, abortController) {
      const res = await appOperation(
        {
          method: 'signRawTransaction',
          params,
          ...businessParams,
        },
        abortController
      );

      return {
        signature: res.signature,
      };
    },
    async transaction(accountId, netId, coin, params, businessParams, abortController) {
      const [chainType, chainId] = splitNetId(netId);

      const res = await appOperation(
        {
          method: 'signTransaction',
          params: {
            accountId,
            chainType,
            chainId,
            contract: coin.contract,
            amountWithDecimal: BIF.from(params.amount).shiftRight(coin.decimal).toString(),
            ...params,
          },
          ...businessParams,
        },
        abortController
      );

      const hash = await useWalletStore.getState().sendTransaction({
        netId,
        signedTx: res.signature,
      });

      return {
        hash,
      };
    },
  },
});

useWalletStore.subscribe(
  (store) => ({
    createAccountTrigger: store.createAccountTrigger,
    deleteAccountTrigger: store.deleteAccountTrigger,
    updateAccountTrigger: store.updateAccountTrigger,
    changePasswordTrigger: store.changePasswordTrigger,
    syncDataTrigger: store.syncDataTrigger,
  }),
  shallowCallback(
    async ({
      createAccountTrigger,
      deleteAccountTrigger,
      updateAccountTrigger,
      changePasswordTrigger,
      syncDataTrigger,
    }) => {
      if (createAccountTrigger) {
        try {
          const { walletCloud } = useSessionStore.getState();
          const { syncBackupCloudData } = useSettingsStore.getState();
          const { accounts } = createAccountTrigger;

          if (!walletCloud) {
            throw new Error('walletCloud not found');
          }
          await syncBackupCloudData({
            type: SyncBackupType.WALLET,
            info: { accounts, updateTime: Date.now(), isChangedPassword: true },
          });
          createAccountTrigger.resolve();
        } catch (err) {
          console.error(err);
          createAccountTrigger.reject(err);
        }
      }

      if (deleteAccountTrigger) {
        try {
          const { walletCloud } = useSessionStore.getState();
          const { syncBackupCloudData } = useSettingsStore.getState();
          const { accountId, accounts } = deleteAccountTrigger;

          if (!walletCloud) {
            throw new Error('walletCloud not found');
          }

          await syncBackupCloudData({
            type: SyncBackupType.WALLET,
            info: {
              deleted_accounts: [accountId],
              accounts,
              updateTime: Date.now(),
              isChangedPassword: true,
            },
          });
          deleteAccountTrigger.resolve();
        } catch (err) {
          console.error(err);
          deleteAccountTrigger.reject(err);
        }
      }

      if (updateAccountTrigger) {
        try {
          const { walletCloud } = useSessionStore.getState();
          const { syncBackupCloudData } = useSettingsStore.getState();
          const { options, accounts } = updateAccountTrigger;

          if (!walletCloud) {
            throw new Error('walletCloud not found');
          }

          const info: any = { accounts, updateTime: Date.now(), isChangedPassword: true };
          if (options?.type === 'DeleteDerivedWallet' && options?.derivedWalletId) {
            info.deleted_wallets = [options?.derivedWalletId];
          }

          await syncBackupCloudData({
            type: SyncBackupType.WALLET,
            info,
          });
          updateAccountTrigger.resolve();
        } catch (err) {
          console.error(err);
          updateAccountTrigger.reject(err);
        }
      }

      if (changePasswordTrigger) {
        try {
          const { walletCloud } = useSessionStore.getState();
          const { syncBackupCloudData } = useSettingsStore.getState();
          const { accounts } = changePasswordTrigger;

          if (!walletCloud) {
            throw new Error('walletCloud not found');
          }

          await syncBackupCloudData({
            type: SyncBackupType.WALLET,
            info: { accounts, updateTime: Date.now(), isChangedPassword: true },
          });
          changePasswordTrigger.resolve();
        } catch (err) {
          console.error(err);
          changePasswordTrigger.reject(err);
        }
      }

      if (syncDataTrigger) {
        try {
          const { walletCloud } = useSessionStore.getState();
          const { syncBackupCloudData } = useSettingsStore.getState();
          const { accounts } = syncDataTrigger;

          if (!walletCloud) {
            throw new Error('walletCloud not found');
          }
          await syncBackupCloudData({ type: SyncBackupType.WALLET, info: { accounts } });
          syncDataTrigger.resolve();
        } catch (err) {
          console.error(err);
          syncDataTrigger.reject(err);
        }
      }
    }
  )
);
